import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Heart, Sparkles, Compass, GraduationCap, Users, ArrowRight } from "lucide-react";
import { DonateButton } from "@/components/DonateButton";
import { AUTOBOOKS_URL } from "@/lib/config";

const IMPACT = [
  { amount: "$25", title: "Friend of AGG", desc: "Provides learning notebooks & journals for a girl's growth." },
  { amount: "$50", title: "Confidence Sponsor", desc: "Sponsors a complete self-care & wellness kit." },
  { amount: "$100", title: "Sisterhood Leader", desc: "Funds direct career mentorship support circles." },
  { amount: "$250", title: "Life Readiness Champion", desc: "Sponsors leadership curriculum books & modules." },
  { amount: "$500", title: "Empowerment Partner", desc: "Sponsors a full workshop experience for five girls." },
  { amount: "$1,000", title: "Transformation Champion", desc: "Enables scholarship, mentoring, and ongoing support." },
];

const REASONS = [
  { icon: Sparkles, label: "Build Confidence" },
  { icon: Users, label: "Develop Leadership" },
  { icon: Compass, label: "Learn Life Readiness" },
  { icon: GraduationCap, label: "Explore College & Careers" },
];

const GivePage = () => {
  return (
    <div className="py-24 max-w-5xl mx-auto px-6 text-left space-y-20 animate-fade-in" data-testid="donate-page">
      {/* Hero */}
      <div className="space-y-5 text-center max-w-2xl mx-auto">
        <span className="font-sans text-xs font-semibold uppercase tracking-widest text-[#C8A96A]">{"Champion The Future"}</span>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-[#4B1E5A]">
          {"Your Gift Becomes Her Future"}
        </h1>
        <p className="font-sans text-base text-[#4A4A4A] leading-relaxed">
          {"Every tax-deductible gift helps fund workshops, mentorship, and life-readiness programming for girls who deserve to believe in their own possibility."}
        </p>
        <div className="flex flex-col items-center gap-3 pt-4">
          <DonateButton label="Give Securely via AutoBooks" variant="large" showTagline={false} testId="give-page-autobooks-btn" />
          <span className="inline-flex items-center gap-1.5 font-sans text-xs text-[#4A4A4A]">
            <ShieldCheck className="h-3.5 w-3.5 text-[#E94F9A]" />
            {"Secure giving powered by AutoBooks"}
          </span>
        </div>
      </div>

      {/* Impact tiers (informational) */}
      <div className="space-y-8">
        <div className="text-center space-y-2 max-w-xl mx-auto">
          <h2 className="font-serif text-2xl sm:text-3xl font-bold text-[#4B1E5A]">{"What Your Gift Makes Possible"}</h2>
          <p className="font-sans text-sm text-[#4A4A4A]">{"Choose the impact that speaks to you — then give the amount that's right for you on our secure AutoBooks page."}</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {IMPACT.map((tier) => (
            <a
              key={tier.amount}
              href={AUTOBOOKS_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`give-tier-${tier.amount.replace(/[^0-9]/g, "")}`}
              className="group border border-[#E5E5E5]/60 bg-white rounded-2xl p-6 hover:border-[#E94F9A]/40 hover:shadow-md hover:-translate-y-1 transition-all"
            >
              <p className="font-serif text-3xl font-bold text-[#E94F9A]">{tier.amount}</p>
              <p className="font-serif text-sm font-semibold text-[#4B1E5A] mt-2">{tier.title}</p>
              <p className="font-sans text-xs text-[#4A4A4A] mt-1.5 leading-relaxed">{tier.desc}</p>
            </a>
          ))}
        </div>
      </div>

      {/* Why your gift matters */}
      <div className="bg-[#FFF5F9]/30 border border-[#FFF5F9] rounded-[3rem] p-8 md:p-12 space-y-8">
        <div className="text-center space-y-2 max-w-xl mx-auto">
          <h2 className="font-serif text-2xl sm:text-3xl font-bold text-[#4B1E5A]">{"Why Your Gift Matters"}</h2>
          <p className="font-sans text-sm text-[#4A4A4A]">{"Your generosity helps girls do more than dream — it equips them to lead."}</p>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
          {REASONS.map((r) => (
            <div key={r.label} className="bg-white rounded-2xl p-6 text-center space-y-3 border border-[#E5E5E5]/40">
              <div className="h-12 w-12 rounded-2xl bg-[#FFF5F9] flex items-center justify-center mx-auto">
                <r.icon className="h-5 w-5 text-[#E94F9A]" />
              </div>
              <p className="font-serif text-sm font-semibold text-[#4B1E5A]">{r.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* More ways to give */}
      <div className="text-center space-y-4">
        <h2 className="font-serif text-2xl font-bold text-[#4B1E5A]">{"Prefer Another Way to Give?"}</h2>
        <p className="font-sans text-sm text-[#4A4A4A] max-w-lg mx-auto">{"From corporate sponsorships to shopping with purpose, there are many ways to invest in AGG girls."}</p>
        <Link
          to="/ways-to-give"
          data-testid="give-explore-ways-btn"
          className="inline-flex items-center gap-2 border border-[#4B1E5A] text-[#4B1E5A] rounded-full px-7 py-3 text-sm font-semibold hover:bg-[#4B1E5A] hover:text-white transition-all"
        >
          {"Explore Ways to Give"} <ArrowRight className="h-4 w-4" />
        </Link>
      </div>

      {/* Trust */}
      <div className="text-center">
        <p className="inline-flex items-center gap-2 text-[11px] text-white/90 bg-[#4B1E5A] px-5 py-2.5 rounded-full font-sans tracking-wide">
          <Heart className="h-3.5 w-3.5 text-[#E94F9A]" />
          {"Tax-Deductible 501(c)(3) Initiative — EIN displayed upon issuance"}
        </p>
      </div>
    </div>
  );
};

export default GivePage;
